import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText, slugify } from "../../lib/api";
import { fallbackImages } from "../../config/pitch";
import { Card, Field, inputCls } from "../../components/admin/Field";
import { ImageField } from "../../components/admin/ImageField";

const empty = { slug: "", customerName: "", vesselName: "", vesselStory: "", headlineLines: ["", "inspection-ready", "every single day."], heroImage: "", closingImage: "", helmImage: "" };

const photos = [
  ["heroImage", "Opening photo", "The big first screen. Upright (portrait) photos of the vessel work best."],
  ["closingImage", "\"Let's talk\" photo", "Shown softly behind the contact section. Wide shots work well."],
  ["helmImage", "Inspection pack photo", "Shown beside the inspection pack. Only use crew photos with permission."],
];

export default function PitchEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(slug ? null : empty);
  const [slugTouched, setSlugTouched] = useState(Boolean(slug));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!slug) return undefined;
    api.get(`/pitches/${slug}`).then((r) => setForm({ ...empty, ...r.data })).catch((e) => { toast.error(errorText(e)); navigate("/admin"); });
    return undefined;
  }, [slug, navigate]);

  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-slate-500" />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "customerName" && !slugTouched ? { slug: slugify(v) } : {}) }));
  const setLine = (i, v) => setForm((f) => ({ ...f, headlineLines: f.headlineLines.map((l, j) => (j === i ? v : l)) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { slug: form.slug, customerName: form.customerName, vesselName: form.vesselName, vesselStory: form.vesselStory, headlineLines: form.headlineLines, heroImage: form.heroImage, closingImage: form.closingImage, helmImage: form.helmImage };
      const { data } = slug ? await api.put(`/admin/pitches/${slug}`, body) : await api.post("/admin/pitches", body);
      toast.success("Saved. The page is live.");
      if (data?.slug !== slug) navigate(`/admin/edit/${data.slug}`, { replace: true });
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="pitch-editor-form" className="space-y-6 pb-28">
      <Link to="/admin" data-testid="editor-back-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0B192C]"><ArrowLeft className="h-4 w-4" />All pages</Link>
      <h1 className="font-display text-4xl">{slug ? form.customerName || "Edit page" : "New customer page"}</h1>
      <p className="text-sm text-slate-600">Only use facts and photos the customer has given you.</p>

      <Card title="Customer">
        <Field label="Customer name" count={form.customerName.length} max={80}>
          <input data-testid="field-customer-name" required maxLength={80} value={form.customerName} onChange={(e) => set("customerName", e.target.value)} className={inputCls} placeholder="e.g. Harbour Ferries" />
        </Field>
        <Field label="Vessel name" count={form.vesselName.length} max={60}>
          <input data-testid="field-vessel-name" required maxLength={60} value={form.vesselName} onChange={(e) => set("vesselName", e.target.value)} className={inputCls} placeholder="e.g. MV Example" />
        </Field>
        <Field label="Vessel story (one short line)" count={form.vesselStory.length} max={160}>
          <textarea data-testid="field-vessel-story" required maxLength={160} rows={2} value={form.vesselStory} onChange={(e) => set("vesselStory", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Page link" hint={`${window.location.origin}/p/${form.slug || "…"}${slug ? " · Changing this breaks links you've already sent." : ""}`}>
          <input data-testid="field-slug" required maxLength={60} pattern="[a-z0-9]+(-[a-z0-9]+)*" title="Lowercase letters, numbers and dashes" value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} className={`${inputCls} font-mono`} />
        </Field>
      </Card>

      <Card title="Headline">
        {["Line 1", "Line 2 (gold italics)", "Line 3"].map((l, i) => (
          <Field key={l} label={l} count={form.headlineLines[i].length} max={40}>
            <input data-testid={`field-headline-${i + 1}`} required maxLength={40} value={form.headlineLines[i]} onChange={(e) => setLine(i, e.target.value)} className={`${inputCls} font-display text-lg ${i === 1 ? "italic text-[#9a5a35]" : ""}`} />
          </Field>
        ))}
      </Card>

      <Card title="Photos">
        {photos.map(([k, label, hint]) => (
          <ImageField key={k} testId={`photo-${k}`} label={label} hint={hint} value={form[k]} fallback={fallbackImages[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} />
        ))}
      </Card>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-end gap-3 px-5 py-4 md:px-8">
          {slug ? <a data-testid="editor-view-page" href={`/p/${slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm hover:bg-slate-100"><ExternalLink className="h-4 w-4" />View page</a> : null}
          <button data-testid="editor-save-btn" disabled={saving} className="flex items-center gap-2 rounded-full bg-[#C87D55] px-7 py-3 font-semibold text-[#0B192C] transition-colors duration-200 hover:bg-[#D4A373] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {slug ? "Save changes" : "Create page"}
          </button>
        </div>
      </div>
    </form>
  );
}
